import React, { useState, useEffect } from 'react';

// Certifique-se de que os imports de tipos estão corretos
import { 
    Empreendimento, 
    EmpreendimentoEditPayload, 
    EmpreendimentoUpdate,
    ItemEmpreendimento // Importar o tipo ItemEmpreendimento
} from '../../types/empreendimento'; 
import { EnderecoCreate } from '../../types/endereco';

import { Incorporadora } from '../../types/incorporadora';
import { incorporadoraService } from '../../services/incorporadoraService';
import { enderecoService } from '../../services/enderecoService';
import { itemEmpreendimentoService, empreendimentoService } from '../../services/empreendimentoService'; 
import { Logradouro, LogradouroCreate } from '../../types/endereco'; 

// Importa os novos componentes de formulário
import EmpreendimentoDadosBasicosForm from './componentes/FormsEdit/EmpreendimentoDadosBasicosForm';
import EmpreendimentoEnderecosForm from './componentes/FormsEdit/EmpreendimentoEnderecosForm';
import EmpreendimentoInformacoesEspecificasForm from './componentes/FormsEdit/EmpreendimentoInformacoesEspecificasForm';
import EmpreendimentoTipologiasForm from './componentes/FormsEdit/EmpreendimentoTipologiasForm'; 

// Simulação de tipos para as opções de seleção
interface SelectOption {
    id: number | string;
    nome: string;
}

// Função utilitária para fazer o 'deep update' (MANTIDA)
const setDeepValue = (obj: any, path: string, value: any) => {
    const parts = path.split('.');
    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        if (current[part] === undefined || current[part] === null) {
            current[part] = {}; 
        }
        current = current[part];
    }
    current[parts[parts.length - 1]] = value;
};


interface EmpreendimentoEditFormProps {
    empreendimento: Empreendimento;
    tiposDisponiveis?: SelectOption[]; 
    itensDisponiveis?: SelectOption[]; 
    
    onSave: (data: Partial<EmpreendimentoUpdate>) => Promise<void>; 
    onCancel: () => void;
    onUpdateSuccess: () => void; 
    loading?: boolean;
}

const EmpreendimentoEditForm: React.FC<EmpreendimentoEditFormProps> = ({
    empreendimento,
    onSave,
    onCancel,
    onUpdateSuccess, 
    loading = false,
    tiposDisponiveis = [], 
}) => {  

    // --- ESTADOS DE OPÇÕES ---
    const [fasesOptions, setFasesOptions] = useState<SelectOption[]>([]);
    const [tiposOptions, setTiposOptions] = useState<SelectOption[]>(tiposDisponiveis); 
    const [itensOptions, setItensOptions] = useState<SelectOption[]>([]); 
    const [itensInfraestrutura, setItensInfraestrutura] = useState<SelectOption[]>([]); 
    const [itensSeguranca, setItensSeguranca] = useState<SelectOption[]>([]); 
    const [itensLazer, setItensLazer] = useState<SelectOption[]>([]);
    
    const [tiposParteOptions, setTiposParteOptions] = useState<SelectOption[]>([]);
    const [disponibilidadeOptions, setDisponibilidadeOptions] = useState<SelectOption[]>([]);

    // --- INICIALIZAÇÃO DE DADOS (MANTIDA) ---
    const initialEndereco: EnderecoCreate = {
        logradouro: {
            zipcode: empreendimento.endereco?.logradouro?.zipcode || '',
            nome: empreendimento.endereco?.logradouro?.nome || '',
            cidade: empreendimento.endereco?.logradouro?.cidade?.id || 0,
            bairro: empreendimento.endereco?.logradouro?.bairro?.id || 0,
        },
        numero: empreendimento.endereco?.numero || undefined,
        complemento: empreendimento.endereco?.complemento || '',
        ponto_referencia: empreendimento.endereco?.ponto_referencia || '',
        latitude: empreendimento.endereco?.latitude || '',
        longitude: empreendimento.endereco?.longitude || '',
    };
    
    const initialPayload: EmpreendimentoEditPayload = {
        nome: empreendimento.nome,
        data_entrega: empreendimento.data_entrega,
        data_inicio_comercializacao: empreendimento.data_inicio_comercializacao,
        observacao: empreendimento.observacao,
        area_total: empreendimento.area_total,
        area_construida: empreendimento.area_construida,
        is_lancamento: empreendimento.is_lancamento,
        incorporadora: empreendimento.incorporadora,
        
        elevadores: empreendimento.elevadores,
        heading: empreendimento.heading,
        pitch: empreendimento.pitch,
        fase_empreendimento: empreendimento.fase_empreendimento,
        tipo_empreendimento: [], 
        item_empreendimento: empreendimento.item_empreendimento || [],
        
        partes_empreendimento: undefined, 
        endereco: empreendimento.endereco ? initialEndereco : undefined, 
    }
    
    const [formData, setFormData] = useState<EmpreendimentoEditPayload>(initialPayload);
    const [incorporadoras, setIncorporadoras] = useState<Incorporadora[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Efeito para carregar dados e opções (MANTIDO)
    useEffect(() => {
        carregarDados();
        carregarOptionsEspecificas(); 
        carregarOptionsTipologias(); 
    }, []); 

    const carregarDados = async () => {
        try {
            const incorporadorasData = await incorporadoraService.listarParaSelecao();
            setIncorporadoras(incorporadorasData);
        } catch (err) {
            console.error('Erro ao carregar incorporadoras:', err);
        }
    };

    // --- FUNÇÃO DE CARREGAMENTO DE OPÇÕES (MANTIDA) ---
    const carregarOptionsEspecificas = async () => { 
        
        setFasesOptions([
            { id: 1, nome: 'Planta' },
            { id: 2, nome: 'Obra' },
            { id: 3, nome: 'Pronto' },
        ]);

        const tiposFixos: SelectOption[] = [
            { id: 1, nome: 'Lote' }, 
            { id: 2, nome: 'Apartamento' },
            { id: 3, nome: 'Comercial' }, 
            { id: 4, nome: 'Casa' },
            { id: 5, nome: 'Compacto' },
        ];
        
        const currentTipos = (empreendimento.tipo_empreendimento || []) as (string | number)[];
        
        const optionsFromCurrentTipos: SelectOption[] = currentTipos
            .filter(val => !tiposFixos.some(fixo => fixo.id === val)) 
            .map(val => ({ 
                id: val, 
                nome: String(val) 
            }));
            
        setTiposOptions([...tiposFixos, ...optionsFromCurrentTipos]);
        
        try {
            const categorias = ['infraestrutura', 'segurança', 'lazer'];
            const results = await Promise.all(
                categorias.map(categoria => itemEmpreendimentoService.getItensByCategoria(categoria))
            );
            
            const [itensInfra, itensSeg, itensLaz] = results;
            const todosItens: ItemEmpreendimento[] = [...itensInfra, ...itensSeg, ...itensLaz];
            const uniqueOptionsMap = new Map<number | string, SelectOption>();
            
            todosItens.forEach(item => {
                uniqueOptionsMap.set(item.id, { id: item.id, nome: item.name });
            });

            const currentItemIds = new Set((empreendimento.item_empreendimento || []).map(String));
            
            currentItemIds.forEach(id => {
                if (!uniqueOptionsMap.has(id)) {
                    uniqueOptionsMap.set(id, { id: id, nome: `[Item Salvo - ID ${id}]` });
                }
            });

            setItensOptions(Array.from(uniqueOptionsMap.values()));
            setItensInfraestrutura(itensInfra.map(item => ({ id: item.id, nome: item.name })));
            setItensSeguranca(itensSeg.map(item => ({ id: item.id, nome: item.name })));
            setItensLazer(itensLaz.map(item => ({ id: item.id, nome: item.name })));

        } catch (error) {
            console.error('Erro ao carregar opções de Itens/Infraestrutura:', error);
            const currentItens = (empreendimento.item_empreendimento || []) as (string | number)[];
            const optionsFromCurrentItens: SelectOption[] = currentItens.map(val => ({ 
                id: val, 
                nome: String(val) 
            }));
            setItensOptions(optionsFromCurrentItens);
        }
    };

    const buildFormData = (data: any) => {
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
        const value = data[key];

        if (Array.isArray(value)) {
        // Para listas de IDs, repete a chave
        value.forEach((v) => formData.append(`${key}[]`, String(v)));
        } else if (value !== undefined && value !== null) {
        formData.append(key, String(value));
        }
    });

    return formData;
    };

    
    // --- FUNÇÃO DE CARREGAMENTO DE OPÇÕES DE TIPOLOGIAS (MANTIDA) ---
    const carregarOptionsTipologias = () => {
        setTiposParteOptions([
            { id: 1, nome: 'Apartamento Padrão' }, { id: 2, nome: 'Cobertura' }, { id: 3, nome: 'Loft' },
        ]);
        // Valores alinhados com empreendimento.models.Disponibilidade no backend:
        // 1=Disponível, 2=Vendido, 3=Distratado, 4=Bloqueada. (Antes desta correção,
        // os ids 2 e 3 estavam com os rótulos trocados, então editar uma unidade
        // vendida salvava "Distrato" e vice-versa.)
        setDisponibilidadeOptions([
            { id: 1, nome: 'Disponível' }, { id: 2, nome: 'Vendido' }, { id: 3, nome: 'Distratado' }, { id: 4, nome: 'Bloqueada' },
        ]);
    };

    // --- FUNÇÕES DE INPUT, VALIDAÇÃO E SUBMISSÃO (MANTIDAS) ---
    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => {
            const newFormData = JSON.parse(JSON.stringify(prev)); 
            setDeepValue(newFormData, field, value);
            return newFormData;
        });

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleCepSearch = async (cep: string) => {
        // Lógica de busca de CEP (mantida)
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.nome || formData.nome.trim() === '') {
            newErrors.nome = 'Nome é obrigatório';
        }
        if (formData.area_total !== undefined && (formData.area_total < 0 || isNaN(formData.area_total as number))) {
            newErrors.area_total = 'A área total deve ser maior ou igual a zero.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
        alert('Por favor, corrija os erros no formulário.');
        return;
    }

    const empreendimentoId = empreendimento.id;
    const enderecoId = empreendimento.endereco?.id;
    const enderecoFormData = formData.endereco;
    const logradouroOriginal = empreendimento.endereco?.logradouro as Logradouro | undefined;
    let enderecoOuLogradouroSalvo = false;

    if (!empreendimentoId) {
        console.error('ID do Empreendimento não encontrado. Impossível salvar.');
        alert('Erro: ID do Empreendimento inválido. Recarregue a página.');
        return;
    }

    // --- A. TRATAMENTO DE ENDEREÇO E LOGRADOURO ---
    if (enderecoId && enderecoFormData) {
        const logradouroObj = enderecoFormData.logradouro as any;
        const logradouroId = logradouroObj.id || logradouroOriginal?.id;

        if (logradouroId && typeof logradouroObj === 'object') {
            const payloadLogradouro: Partial<LogradouroCreate> = {
                ...(logradouroObj.cidade !== logradouroOriginal?.cidade?.id && logradouroObj.cidade && { cidade: logradouroObj.cidade }),
                ...(logradouroObj.bairro !== logradouroOriginal?.bairro?.id && logradouroObj.bairro && { bairro: logradouroObj.bairro }),
                ...(logradouroObj.zipcode !== logradouroOriginal?.zipcode && logradouroObj.zipcode && { zipcode: logradouroObj.zipcode }),
                ...(logradouroObj.nome !== logradouroOriginal?.nome && logradouroObj.nome && { nome: logradouroObj.nome }),
            };

            if (Object.keys(payloadLogradouro).length > 0) {
                try {
                    await enderecoService.patchLogradouro(logradouroId, payloadLogradouro);
                    enderecoOuLogradouroSalvo = true;
                } catch (error) {
                    console.error('❌ Erro ao salvar Logradouro:', error);
                    alert('Erro ao salvar Logradouro. O salvamento foi abortado.');
                    return;
                }
            }
        }

        const payloadEndereco: Partial<Omit<EnderecoCreate, 'logradouro'>> = {
            ...(enderecoFormData.numero !== undefined && { numero: enderecoFormData.numero }),
            ...(enderecoFormData.complemento !== undefined && { complemento: enderecoFormData.complemento }),
            ...(enderecoFormData.ponto_referencia !== undefined && { ponto_referencia: enderecoFormData.ponto_referencia }),
            ...(enderecoFormData.latitude !== undefined && { latitude: enderecoFormData.latitude }),
            ...(enderecoFormData.longitude !== undefined && { longitude: enderecoFormData.longitude }),
        };

        if (Object.keys(payloadEndereco).length > 0) {
            try {
                await enderecoService.updateEndereco(enderecoId, payloadEndereco);
                enderecoOuLogradouroSalvo = true;
            } catch (error) {
                console.error('❌ Erro ao salvar Endereço:', error);
                alert('Erro ao salvar Endereço. O salvamento do empreendimento foi abortado.');
                return;
            }
        }
    }

    // --- B. MONTAGEM DO PAYLOAD PRINCIPAL ---
    const payloadToSave: Partial<EmpreendimentoUpdate> = {};
    let hasBasicDataChanges = false;

    const fieldsToMap: (keyof EmpreendimentoUpdate | keyof Empreendimento)[] = [
        'nome', 'area_total', 'area_construida', 'data_entrega',
        'data_inicio_comercializacao', 'heading', 'pitch', 'elevadores',
        'observacao', 'is_lancamento', 'incorporadora',
        'fase_empreendimento', 'tipo_empreendimento', 'item_empreendimento',
    ];

    fieldsToMap.forEach((key) => {
        let formValue = (formData as any)[key];
        const initialValue = (empreendimento as any)[key];

        // --- TRATAMENTO ESPECIAL PARA ARRAYS ---
        if (key === 'tipo_empreendimento' && Array.isArray(formValue)) {
            // Mantém apenas valores válidos e não nulos
            formValue = formValue.filter((v): v is string | number => v !== null && v !== undefined && v !== '');
        } else if (key === 'item_empreendimento' && Array.isArray(formValue)) {
            // Garante array de números válidos
            formValue = formValue
                .map(v => (typeof v === 'string' ? parseInt(v, 10) : v))
                .filter((v): v is number => !isNaN(v));
        }

        // --- CONVERSÃO NUMÉRICA PARA CAMPOS SIMPLES ---
        if (typeof initialValue === 'number' && typeof formValue === 'string') {
            const num = parseFloat(formValue);
            formValue = isNaN(num) ? formValue : num;
        }

        // --- NORMALIZAÇÃO ---
        const normalizedFormValue = Array.isArray(formValue) ? formValue : (formValue === '' ? null : formValue);
        const normalizedInitialValue = Array.isArray(initialValue) ? initialValue : (initialValue === '' ? null : initialValue);

        // --- COMPARAÇÃO E INCLUSÃO NO PAYLOAD ---
        if (Array.isArray(normalizedFormValue) && Array.isArray(normalizedInitialValue)) {
            const formSorted = [...normalizedFormValue].sort();
            const initialSorted = [...normalizedInitialValue].sort();
            if (JSON.stringify(formSorted) !== JSON.stringify(initialSorted)) {
                (payloadToSave as any)[key] = normalizedFormValue;
                hasBasicDataChanges = true;
            }
        } else if (normalizedFormValue !== normalizedInitialValue) {
            (payloadToSave as any)[key] = normalizedFormValue;
            hasBasicDataChanges = true;
        }
    });

    if (Array.isArray(payloadToSave.tipo_empreendimento) && payloadToSave.tipo_empreendimento.length === 0) {
        delete (payloadToSave as any).tipo_empreendimento;
    }

    if (empreendimento.endereco?.id) {
        (payloadToSave as any).endereco = empreendimento.endereco.id;
    }

    // --- C. FINALIZAÇÃO ---
    if (!hasBasicDataChanges) {

       
        if (enderecoOuLogradouroSalvo) {
           

            onUpdateSuccess();
        }
        return;
    }


    

    // --- D. ENVIO VIA FORM DATA ---
    const formDataFinal = new FormData();


    // tipo_empreendimento como IDs
    if (Array.isArray(payloadToSave.tipo_empreendimento)) {
        payloadToSave.tipo_empreendimento.forEach(id => {
            formDataFinal.append('tipo_empreendimento', String(id));
        });
        delete (payloadToSave as any).tipo_empreendimento;
    }

    // item_empreendimento como IDs
    if (Array.isArray(payloadToSave.item_empreendimento)) {
        payloadToSave.item_empreendimento.forEach(id => {
            formDataFinal.append('item_empreendimento', String(id));
        });
        delete (payloadToSave as any).item_empreendimento;
    }

    // Adiciona os demais campos
    Object.entries(payloadToSave).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            formDataFinal.append(key, typeof value === 'object' ? JSON.stringify(value) : value.toString());
        }
    });

    // --- E. CHAMADA DA API ---
    try {
        await empreendimentoService.patchFormData(empreendimento.id, formDataFinal);
        onUpdateSuccess();
    } catch (error) {
        console.error('❌ Erro ao salvar empreendimento:', error);
        alert('Erro ao salvar empreendimento. Veja o console para detalhes.');
    }
};

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* 1. Informações Básicas (MANTIDO) */}
            <EmpreendimentoDadosBasicosForm 
                formData={formData} 
                handleInputChange={handleInputChange} 
                errors={errors} 
                incorporadoras={incorporadoras} 
            />

            {/* 2. Endereços (MANTIDO) */}
            <EmpreendimentoEnderecosForm 
                formData={formData} 
                handleInputChange={handleInputChange} 
                errors={errors}
                onCepSearch={handleCepSearch} 
            />

            {/* 3. Informações Específicas */}
            <EmpreendimentoInformacoesEspecificasForm 
                formData={formData} 
                handleInputChange={handleInputChange} 
                errors={errors}
                fasesOptions={fasesOptions}
                tiposOptions={tiposOptions} 
                itensInfraestrutura={itensInfraestrutura}
                itensSeguranca={itensSeguranca}
                itensLazer={itensLazer}
            />

            {/* 4. Tipologias (MANTIDO) */}
            <EmpreendimentoTipologiasForm 
                empreendimentoId={empreendimento.id} 
                formData={formData} 
                handleInputChange={handleInputChange} 
                errors={errors}
                tiposParteOptions={tiposParteOptions}
                disponibilidadeOptions={disponibilidadeOptions}
            />


            {/* Botões (MANTIDO) */}
            <div className="flex justify-end space-x-3 mt-8">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={loading}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                    {loading ? 'Salvando...' : 'Salvar'}
                </button>
            </div>
        </form>
    );
};

export default EmpreendimentoEditForm;